/**
 * CRO-WEB-180 Experiment Hook
 *
 * Hook that determines whether to redirect users from onboarding to the start page.
 * Returns configuration for the redirect behavior.
 */

import { useExperimentTracking } from "@/src/experiments/useExperimentTracking";
import { logger } from "@/src/utils/logger";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { croWeb180Config } from "./config";
import { CroWeb180Variant, useCroWeb180Variant } from "./getCroWeb180Variant";

export interface CroWeb180ExperimentConfig {
  shouldRedirectToStart: boolean; // true = redirect to /start, false = normal onboarding flow
  variant: CroWeb180Variant | null;
  isLoading: boolean;
}

/**
 * Hook that determines the redirect behavior for CRO-WEB-180 experiment
 * Also handles the actual redirect when needed
 */
export const useCroWeb180Experiment = (): CroWeb180ExperimentConfig => {
  const { variant, isInitialized } = useCroWeb180Variant();
  const router = useRouter();

  // Track experiment participation
  useExperimentTracking(croWeb180Config);

  // Determine if we're still loading the variant
  const isLoading = false;

  // DEBUG: Log the variant value (not in production)
  logger.log(
    "🧪 CRO-WEB-180 Experiment variant:",
    variant,
    "isLoading:",
    isLoading
  );

  // Determine behavior based on variant
  const shouldRedirectToStart = variant === "start";

  // Handle the redirect when needed
  useEffect(() => {
    if (shouldRedirectToStart && isInitialized) {
      logger.log("🔄 CRO-WEB-180 Redirecting to /start");
      router.push("/start");
    }
  }, [shouldRedirectToStart, isInitialized, router]);

  return {
    shouldRedirectToStart,
    variant,
    isLoading,
  };
};
